/**
 * Gloverse — Capacitor Native Bootstrap
 *
 * Wires native plugins for the Android/iOS build:
 *  - Hides the splash screen once React has mounted
 *  - Sets status bar styling (brand pink)
 *  - Routes deep links (gloverse.in/post/xxx, gloverse://...) to react-router
 *  - Handles Android hardware back button
 *
 * Safe on web: if Capacitor isn't available, all calls are no-ops.
 */

let isNative = false;
try {
  // @capacitor/core exports Capacitor runtime
  // eslint-disable-next-line global-require
  const { Capacitor } = require('@capacitor/core');
  isNative = Capacitor?.isNativePlatform?.() === true;
} catch {
  isNative = false;
}

export const IS_NATIVE = isNative;

/**
 * Initialize native plugins.
 * @param {Function} navigate - react-router's navigate function
 */
export async function initCapacitor(navigate) {
  if (!isNative) return;

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    // Hide splash after React is ready
    setTimeout(() => SplashScreen.hide().catch(() => {}), 500);
  } catch { /* plugin missing */ }

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: '#ec4899' }).catch(() => {});
  } catch { /* plugin missing */ }

  try {
    const { App: CapApp } = await import('@capacitor/app');

    // Deep links — extract the path from the URL and navigate inside the SPA
    CapApp.addListener('appUrlOpen', ({ url }) => {
      try {
        // Handle both gloverse.in URLs and custom gloverse:// scheme
        let path = '/';
        if (url.startsWith('gloverse://')) {
          path = '/' + url.replace('gloverse://', '');
        } else {
          const parsed = new URL(url);
          path = parsed.pathname + parsed.search + parsed.hash;
        }
        if (path && path !== '/') navigate(path);
      } catch (e) {
        console.error('Deep link parse error:', e);
      }
    });

    // Android hardware back button — go back in history, or exit if on root
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });
  } catch { /* plugin missing */ }
}
